import { preserveSectionStyles } from './section-styles.js';

const MENU_SELECTOR = 'header-menu';
const CLICK_TRIGGER = 'click';
const ACTIVE_OVERLAY_ATTR = 'data-ch-overlay-active';

const menuState = new WeakMap();
const fetchedHeaderSections = new Map();
const overlayResizeObserver =
  typeof ResizeObserver === 'function' ? new ResizeObserver(() => scheduleActiveOverlayOffset()) : null;

let activeOverlayMenu = null;
let overlayFrame = 0;
let menuHeightFrame = 0;
let overlayListenersActive = false;

function getMenuItem(target) {
  if (!(target instanceof Element)) return null;

  return target.closest('.menu-list__list-item');
}

function getActiveItem(menu) {
  const activeLink = menu.querySelector('.menu-list__link[aria-expanded="true"]');

  return getMenuItem(activeLink);
}

function getMenuLink(item) {
  return item?.querySelector('.menu-list__link[aria-haspopup="true"]') ?? null;
}

function getSubmenu(item) {
  return item?.querySelector('[ref="submenu[]"]') ?? null;
}

function getOverflow(menu) {
  return menu.refs?.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]') ?? null;
}

function getOverflowTriggerItem(menu, item) {
  if (item?.slot !== 'more') return item;

  return menu.querySelector('.menu-list__list-item[slot="overflow"]');
}

function hasSubmenu(item) {
  return item?.slot === 'more' || getMenuLink(item) !== null;
}

function getHeaderSection(menu) {
  return menu.closest('.shopify-section[id^="shopify-section-"]');
}

function getHeaderSectionId(menu) {
  return getHeaderSection(menu)?.id?.replace(/^shopify-section-/, '') || '';
}

function getSectionRenderingURL(sectionId) {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  url.searchParams.set('section_id', sectionId);
  url.searchParams.sort();
  return url.toString();
}

async function fetchHeaderSection(menu) {
  const sectionId = getHeaderSectionId(menu);
  if (!sectionId) return null;

  const url = getSectionRenderingURL(sectionId);
  let sectionPromise = fetchedHeaderSections.get(url);

  if (!sectionPromise) {
    sectionPromise = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Header section fetch failed: ${response.status}`);
        return response.text();
      })
      .then((html) => new DOMParser().parseFromString(html, 'text/html'))
      .catch(() => null);
    fetchedHeaderSections.set(url, sectionPromise);
  }

  return sectionPromise;
}

async function hydrateSubmenuContent(item) {
  const menu = item?.closest(MENU_SELECTOR);
  const submenu = getSubmenu(item);

  if (!(menu instanceof HTMLElement) || !(submenu instanceof HTMLElement)) return;
  if (submenu.dataset.chHeaderContentLoaded === 'true') return;
  if (!submenu.querySelector('.mega-menu__content-list--products')) return;
  if (submenu.querySelector('.mega-menu__content-list--products .resource-card')) {
    submenu.dataset.chHeaderContentLoaded = 'true';
    return;
  }

  submenu.dataset.chHeaderContentLoading = 'true';

  const remoteDocument = await fetchHeaderSection(menu);
  const submenuId = submenu.querySelector('.menu-list__submenu-inner')?.id;
  const remoteSubmenu = submenuId ? remoteDocument?.getElementById(submenuId) : null;
  const localContent = submenu.querySelector('.mega-menu__content');
  const remoteContent = remoteSubmenu?.querySelector('.mega-menu__content');

  if (!(localContent instanceof HTMLElement) || !(remoteContent instanceof HTMLElement)) {
    delete submenu.dataset.chHeaderContentLoading;
    return;
  }

  preserveSectionStyles(remoteDocument, getHeaderSection(menu) || menu);
  localContent.replaceWith(remoteContent.cloneNode(true));
  submenu.dataset.chHeaderContentLoaded = 'true';
  delete submenu.dataset.chHeaderContentLoading;
  scheduleOpenMenuHeights();
}

function scheduleSubmenuHydration(target) {
  const item = getMenuItem(target);
  if (!hasSubmenu(item)) return;

  hydrateSubmenuContent(item);
}

function setActiveFeaturePanel(target) {
  if (!(target instanceof Element)) return;

  const featureTrigger = target.closest('[data-ch-feature-trigger]');
  if (!featureTrigger) return;

  const featureColumn = featureTrigger.closest('[data-ch-feature-index]');
  if (!featureColumn) return;

  const grid = featureColumn.closest('[data-menu-grid-id]');
  if (!(grid instanceof HTMLElement)) return;

  const featureIndex = featureColumn.getAttribute('data-ch-feature-index') ?? '';
  if (grid.dataset.chActiveFeature === featureIndex) return;

  grid.dataset.chActiveFeature = featureIndex;
  scheduleOpenMenuHeights();
}

function setOverlayState(menu) {
  const isOverlayEnabled = menu.dataset.chOverlayEnabled === 'true';
  const isOpen = menu.getAttribute('aria-expanded') === 'true';
  const overlay = getOverlay(menu);

  if (isOverlayEnabled && isOpen) {
    menu.setAttribute(ACTIVE_OVERLAY_ATTR, 'true');
    overlay?.setAttribute(ACTIVE_OVERLAY_ATTR, 'true');
    activateOverlay(menu);
  } else {
    menu.removeAttribute(ACTIVE_OVERLAY_ATTR);
    overlay?.removeAttribute(ACTIVE_OVERLAY_ATTR);
    deactivateOverlay(menu);
  }
}

function getOverlay(menu) {
  const state = menuState.get(menu);
  if (state?.overlay) return state.overlay;

  const overlay = menu.nextElementSibling;

  return overlay?.matches('[data-ch-header-menu-overlay]') ? overlay : null;
}

function detachOverlay(menu) {
  const overlay = getOverlay(menu);

  if (!(overlay instanceof HTMLElement) || overlay.parentElement === document.body) return overlay;

  document.body.append(overlay);

  return overlay;
}

function getHeaderBottom(menu) {
  const header = menu.closest('header-component') ?? document.querySelector('#header-component');

  if (!(header instanceof HTMLElement)) return 0;

  return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
}

function updateOverlayOffset(menu) {
  const overlay = getOverlay(menu);

  if (!(overlay instanceof HTMLElement)) return;

  overlay.style.setProperty('--ch-header-menu-overlay-top', `${getHeaderBottom(menu)}px`);
}

function scheduleActiveOverlayOffset() {
  if (!activeOverlayMenu || overlayFrame) return;

  overlayFrame = requestAnimationFrame(() => {
    overlayFrame = 0;
    if (activeOverlayMenu) updateOverlayOffset(activeOverlayMenu);
  });
}

function updateOpenMenuHeight(menu) {
  const activeItem = getActiveItem(menu);
  const submenu = getSubmenu(activeItem);
  const overflow = getOverflow(menu);

  if (!activeItem && !overflow) return;

  menu.style.setProperty(
    '--submenu-height',
    `${Math.max(submenu instanceof HTMLElement ? submenu.offsetHeight : 0, overflow instanceof HTMLElement ? overflow.offsetHeight : 0)}px`
  );
}

function scheduleOpenMenuHeights() {
  if (menuHeightFrame) return;

  menuHeightFrame = requestAnimationFrame(() => {
    menuHeightFrame = 0;
    document.querySelectorAll(`${MENU_SELECTOR}[aria-expanded="true"]`).forEach(updateOpenMenuHeight);
  });
}

function activateOverlay(menu) {
  activeOverlayMenu = menu;
  updateOverlayOffset(menu);

  if (!overlayListenersActive) {
    window.addEventListener('scroll', scheduleActiveOverlayOffset, { passive: true });
    window.addEventListener('resize', scheduleActiveOverlayOffset);
    overlayListenersActive = true;
  }

  const header = menu.closest('header-component') ?? document.querySelector('#header-component');
  if (header instanceof HTMLElement) overlayResizeObserver?.observe(header);
}

function deactivateOverlay(menu) {
  if (activeOverlayMenu !== menu) return;

  getOverlay(menu)?.style.removeProperty('--ch-header-menu-overlay-top');
  activeOverlayMenu = null;

  if (overlayFrame) {
    cancelAnimationFrame(overlayFrame);
    overlayFrame = 0;
  }

  if (overlayListenersActive) {
    window.removeEventListener('scroll', scheduleActiveOverlayOffset);
    window.removeEventListener('resize', scheduleActiveOverlayOffset);
    overlayListenersActive = false;
  }

  overlayResizeObserver?.disconnect();
}

function closeMenu(menu) {
  if (menu.dataset.chDropdownTrigger === CLICK_TRIGGER) {
    closeClickMenu(menu);
    return;
  }

  const activeItem = getActiveItem(menu);

  if (activeItem && typeof menu.deactivate === 'function') {
    menu.deactivate({ target: activeItem });
  }
}

function closeClickMenu(menu) {
  menu.querySelectorAll('.menu-list__link[aria-expanded="true"]').forEach((link) => {
    link.setAttribute('aria-expanded', 'false');
    link.removeAttribute('data-animating');
  });

  menu.style.setProperty('--submenu-height', '0px');
  menu.style.setProperty('--submenu-opacity', '0');
  menu.dataset.overflowExpanded = 'false';
  menu.setAttribute('aria-expanded', 'false');
  menu.removeAttribute(ACTIVE_OVERLAY_ATTR);
}

function openClickMenu(menu, requestedItem) {
  const item = getOverflowTriggerItem(menu, requestedItem);
  const link = getMenuLink(item);
  const submenu = getSubmenu(item);
  const overflow = getOverflow(menu);

  if (!link) return;

  hydrateSubmenuContent(item);
  closeClickMenu(menu);

  link.setAttribute('aria-expanded', 'true');
  menu.dataset.overflowExpanded = (item?.slot !== '').toString();
  menu.setAttribute('aria-expanded', 'true');
  menu.style.setProperty(
    '--submenu-height',
    `${Math.max(submenu instanceof HTMLElement ? submenu.offsetHeight : 0, overflow instanceof HTMLElement ? overflow.offsetHeight : 0)}px`
  );
  menu.style.setProperty('--submenu-opacity', '1');
  setOverlayState(menu);
}

function toggleClickMenuItem(menu, item) {
  if (getActiveItem(menu) === getOverflowTriggerItem(menu, item)) {
    closeClickMenu(menu);
    return;
  }

  openClickMenu(menu, item);
}

function initMenu(menu) {
  if (menuState.has(menu)) return;

  const overlay = detachOverlay(menu);
  const observer = new MutationObserver(() => setOverlayState(menu));

  observer.observe(menu, {
    attributes: true,
    attributeFilter: ['aria-expanded', 'data-ch-overlay-enabled'],
    subtree: true,
  });

  menuState.set(menu, { observer, overlay });
  setOverlayState(menu);
}

function initMenus() {
  document.querySelectorAll(MENU_SELECTOR).forEach(initMenu);
}

function handleClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const overlay = target.closest('[data-ch-header-menu-overlay]');
  if (overlay) {
    if (activeOverlayMenu) closeMenu(activeOverlayMenu);
    return;
  }

  const menu = target.closest(MENU_SELECTOR);
  if (!menu) {
    document.querySelectorAll(`${MENU_SELECTOR}[aria-expanded="true"]`).forEach(closeMenu);
    return;
  }

  if (menu.dataset.chDropdownTrigger !== CLICK_TRIGGER) return;

  const trigger = target.closest('.menu-list__link');
  const item = getMenuItem(trigger);
  if (!hasSubmenu(item)) return;

  event.preventDefault();
  toggleClickMenuItem(menu, item);
}

function handleKeydown(event) {
  if (event.key === 'Escape') {
    document.querySelectorAll(`${MENU_SELECTOR}[aria-expanded="true"]`).forEach(closeMenu);
    return;
  }

  if (event.key !== ' ' && event.key !== 'Spacebar') return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const menu = target.closest(MENU_SELECTOR);
  if (menu?.dataset.chDropdownTrigger !== CLICK_TRIGGER) return;

  const trigger = target.closest('.menu-list__link');
  const item = getMenuItem(trigger);
  if (!hasSubmenu(item)) return;

  event.preventDefault();
  toggleClickMenuItem(menu, item);
}

customElements.whenDefined('header-menu').then(initMenus);
document.addEventListener('shopify:section:load', initMenus);
window.addEventListener('resize', scheduleOpenMenuHeights);
document.addEventListener('click', handleClick, true);
document.addEventListener('keydown', handleKeydown);
document.addEventListener(
  'pointerover',
  (event) => {
    scheduleSubmenuHydration(event.target);
    setActiveFeaturePanel(event.target);
  },
  true
);
document.addEventListener(
  'mouseover',
  (event) => {
    scheduleSubmenuHydration(event.target);
    setActiveFeaturePanel(event.target);
  },
  true
);
document.addEventListener('mousemove', (event) => setActiveFeaturePanel(event.target), true);
document.addEventListener(
  'focusin',
  (event) => {
    scheduleSubmenuHydration(event.target);
    setActiveFeaturePanel(event.target);
  },
  true
);
